import type { MutationCallbacks } from '#shared'
import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/vue-query'
import { unref, type MaybeRef } from 'vue'
import { getChatQueryKey, PERSONAL_CHATS_QUERY_KEY, PUBLIC_CHATS_QUERY_KEY } from '#entities/chat'
import type { GetChatsResponse } from '#entities/chat/model/get-chats-response'
import { setUnreadCount } from '../api/set-unread-count'
import type { SetUnreadCountResponse } from '../models/set-unread-count-response'

type SetUnreadCountMutatuonCallbacks = MutationCallbacks<number, SetUnreadCountResponse>

export function useSetUnreadCountMutation(chatId: MaybeRef<number>, callbacks: SetUnreadCountMutatuonCallbacks = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    ...callbacks,

    mutationFn: (unreadCount: number) => setUnreadCount(unref(chatId), unreadCount),

    onMutate: async unreadCount => {
      queryClient.setQueryData(PUBLIC_CHATS_QUERY_KEY, (queryData: GetChatsResponse | undefined) => {
        if (!queryData) return

        const newData = queryData?.data.map(chat => {
          if (chat.id === unref(chatId)) {
            return { ...chat, unreadCount }
          }

          return chat
        })

        return {
          ...queryData,
          data: newData,
        }
      })

      queryClient.setQueryData(PERSONAL_CHATS_QUERY_KEY, (queryData: InfiniteData<GetChatsResponse> | undefined) => {
        if (!queryData)
          return {
            pageParams: [],
            pages: [],
          }

        const newPages = queryData?.pages.map(page => {
          const newData = page.data.map(chat => {
            if (chat.id === unref(chatId)) {
              return { ...chat, unreadCount }
            }

            return chat
          })

          return { ...page, data: newData }
        })

        return {
          ...queryData,
          pages: newPages,
        }
      })

      await queryClient.invalidateQueries({ queryKey: getChatQueryKey(chatId), exact: true })
    },
  })
}
